import React, { useMemo } from 'react'

import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyContentSections,
  CompanyPageLayout,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import { InitialDoorData } from 'components/Samples/SampleRequestFormModal'

import { CompanyTypeSlug } from 'models/companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { buildGooglePlaceSummaryContent } from './utils/googlePlacesHelpers'
import { buildLocationPlacesContent } from './utils/locationPlacesHelper'
import useDocumentTitle from 'components/useDocumentTitle'
import { useHideFooter } from 'context/layout'

export function NewConvenienceLocationPage({
  convenienceID,
}: {
  convenienceID: number
}) {
  const api = apiService()
  const { data: doorProxyData, isLoading } =
    api.useGetConvenienceDoor(convenienceID)
  const { preferences } = usePreferences()

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  const chainProxyId = doorProxyData?.chain_proxy.id
  const { data: companyData } = api.useGetCompany(chainProxyId ?? 0)

  // Set document title
  useDocumentTitle(
    `${doorProxyData?.cstore?.name || 'Not Found'} (Convenience Store Door) - First Bite`
  )

  // Modal state management
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  // Pre-compute door data for sample requests to avoid redundant API calls
  // The data is already loaded via useGetConvenienceDoor
  const initialDoorData: InitialDoorData | undefined = useMemo(() => {
    if (!doorProxyData?.cstore || !companyData) return undefined

    const cstore = doorProxyData.cstore
    return {
      name: cstore.name || '',
      address: cstore.address || '',
      city: cstore.city || '',
      state: cstore.state || '',
      zipcode: cstore.zipcode || '',
      companyName: companyData.name || '',
    }
  }, [doorProxyData?.cstore, companyData])

  const entityBreadcrumbs = useMemo(
    () => [
      ...(companyData?.name
        ? [
            {
              title: 'Company',
              label: companyData.name,
              path: `/convenience/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Convenience]}${companyData?.cstore_id}`,
            },
          ]
        : []),
      ...(doorProxyData?.cstore?.name
        ? [{ title: 'Door', label: doorProxyData.cstore.name }]
        : []),
    ],
    [
      companyData?.name,
      companyData?.cstore_id,
      preferences?.company_type_prefix_map,
      doorProxyData?.cstore?.name,
    ]
  )

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  const summaryTabContent = buildGooglePlaceSummaryContent({
    placeData: doorProxyData?.cstore,
    onViewDoorsClick: () => setShowDoorsModal(true),
    hideDoorsMetric: true,
  })

  const locationDetailsTabContent = buildLocationPlacesContent({
    addressInfo: {
      city: doorProxyData?.cstore?.city,
      state: doorProxyData?.cstore?.state,
      street: doorProxyData?.cstore?.address,
      zipcode: doorProxyData?.cstore?.zipcode,
    },
    phone: doorProxyData?.cstore?.phone,
    googlePlaceUrl: doorProxyData?.cstore?.place_url,
  })

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData?.id as number}
        breadcrumbRoot={companyData?.sub_category?.name || 'Convenience Stores'}
        breadcrumbs={[
          {
            title: 'Store',
            breadcrumb: companyData?.name || '',
            url: `/convenience/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Convenience]}${companyData?.cstore_id}`,
          },
          {
            title: 'Door',
            breadcrumb: doorProxyData?.cstore?.name || '',
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        sidebar={{
          header: {
            name: doorProxyData?.cstore?.name || '',
            domain: companyData?.domain || '',
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData?.id as number}
          doorProxyId={doorProxyData?.id}
          companyName={doorProxyData?.cstore?.name || ''}
          websiteAddress={companyData?.domain || ''}
          companyTypeSlug={companyData?.sub_category?.slug as CompanyTypeSlug}
          tableTypeSlug="convenience-doors"
          isUserCreated={false}
          isDoorPage={true}
          initialDoorData={initialDoorData}
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {companyData && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={companyData?.id as number}
          chainData={{
            id: companyData?.id as number,
            name: companyData?.name || '',
            domain: companyData?.domain || '',
          }}
          subCategory={companyData?.sub_category}
          companyName={companyData?.name || ''}
        />
      )}
    </>
  )
}
